import breadcrumbs from "../helperDataset/breadcrumbs.json";

type BreadcrumbItem = {
  name: string;
  url: string;
  icon: string;
  navigation: string;
  show: boolean;
};

type LocationLike = {
  pathname: string;
};

const ALL_BREADCRUMBS: BreadcrumbItem[] = [
  ...breadcrumbs.flatMap((menu: any) => menu.dropdown || []),
  ...breadcrumbs,
];

const findBreadcrumbByUrl = (url: string) =>
  ALL_BREADCRUMBS.find((item) => item.url === url);

const pathParts = (pathname: string) => pathname.split("/").filter(Boolean);

const createBreadcrumb = (
  name: string,
  url: string,
  icon: string,
  navigation: string,
  show = true
): BreadcrumbItem => ({
  name,
  url,
  icon,
  navigation,
  show,
});

export function BreadCrumbGenerator(location: LocationLike): BreadcrumbItem[] {
  const parts = pathParts(location.pathname);
  const [section, subSection, action, id] = parts;

  if (!section) return [];

  if (section === "update-password") {
    return [
      createBreadcrumb(
        "Update Password",
        "update-password",
        "updatePassword",
        "update-password"
      ),
    ];
  }

  if (section === "leads" && subSection === "new-lead") {
    return [
      createBreadcrumb("New Lead", "new-lead", "newLead", "leads/new-lead"),
    ];
  }

  if (section === "customers") {
    if (subSection === "edit") {
      return [
        createBreadcrumb("Customers", "customers", "customers", "/customers"),
        createBreadcrumb(
          "Update Customer Details",
          "customers",
          "editIcon",
          `/customers/edit/${id}`,
          false
        ),
      ];
    }

    if (subSection) {
      return [
        createBreadcrumb("Customers", "customers", "customers", "/customers"),
        createBreadcrumb(
          "Customer Details",
          ":id",
          "customers",
          `/customers/${subSection}`,
          false
        ),
      ];
    }

    const customerBreadcrumb = findBreadcrumbByUrl("customers");
    return customerBreadcrumb ? [customerBreadcrumb] : [];
  }

  if (section === "user-profile") {
    if (subSection === "edit") {
      return [
        createBreadcrumb(
          "User Profile",
          "user-profile",
          "userProfile",
          "/user-profile"
        ),
        createBreadcrumb(
          "Update User Profile Details",
          "user-profile",
          "editIcon",
          `/user-profile/edit/${id}`,
          false
        ),
      ];
    }

    const userProfileBreadcrumb = findBreadcrumbByUrl("user-profile");
    return userProfileBreadcrumb ? [userProfileBreadcrumb] : [];
  }

  if (section === "jobs") {
    if (subSection === "add-job") {
      return [
        createBreadcrumb("Jobs", "jobs", "jobs2", "/jobs"),
        createBreadcrumb(
          "Add new Job",
          "add-job",
          "addJob",
          "/jobs/add-job",
          false
        ),
      ];
    }

    if (subSection === "edit") {
      return [
        createBreadcrumb("Jobs", "jobs", "jobs", "/jobs"),
        createBreadcrumb(
          "Update Job Detail",
          "add-job",
          "editIcon",
          `/jobs/edit/${id}`,
          false
        ),
      ];
    }

    if (subSection) {
      return [
        createBreadcrumb("Jobs", "jobs", "jobs", "/jobs"),
        createBreadcrumb(
          "Job Details",
          ":id",
          "jobs",
          `/jobs/${subSection}`,
          false
        ),
      ];
    }

    const jobsBreadcrumb = findBreadcrumbByUrl("jobs");
    return jobsBreadcrumb ? [jobsBreadcrumb] : [];
  }

  if (section === "leads") {
    const leadConfig: Record<
      string,
      {
        title: string;
        icon: string;
        listPath: string;
        detailsUrl: string;
      }
    > = {
      "all-leads": {
        title: "All Leads",
        icon: "leads",
        listPath: "leads/all-leads",
        detailsUrl: "all-leads/:id",
      },
      "hot-leads": {
        title: "High Intent Leads",
        icon: "verifiedLeads",
        listPath: "leads/hot-leads",
        detailsUrl: "hot-leads/:id",
      },
      "warm-leads": {
        title: "Medium Intent Leads",
        icon: "rejectedLeads",
        listPath: "leads/warm-leads",
        detailsUrl: "warm-leads/:id",
      },
      "interested-leads": {
        title: "Low Intent Leads",
        icon: "errorLeads",
        listPath: "leads/interested-leads",
        detailsUrl: "interested-leads/:id",
      },
    };

    const config = subSection ? leadConfig[subSection] : undefined;

    if (config) {
      if (parts.length === 2) {
        const match = findBreadcrumbByUrl(subSection);
        return match
          ? [match]
          : [
              createBreadcrumb(
                config.title,
                subSection,
                config.icon,
                config.listPath
              ),
            ];
      }

      return [
        createBreadcrumb(
          config.title,
          subSection,
          config.icon,
          config.listPath
        ),
        createBreadcrumb("Lead details", config.detailsUrl, "leads", "", false),
      ];
    }
  }

  if (section === "user") {
    if (subSection === "users" && parts.length === 2) {
      return [createBreadcrumb("Users", "users", "userManagement", "", true)];
    }

    if (subSection === "requests" && parts.length === 2) {
      return [
        createBreadcrumb("Requests", "requests", "viewProfile2", "", true),
      ];
    }

    if (subSection === "add-user") {
      return [
        createBreadcrumb(
          "Add User",
          "add-user",
          "addUser",
          "/user/add-user",
          false
        ),
      ];
    }

    if (subSection === "users" && action === "view-profile") {
      return [
        createBreadcrumb("Users", "users", "userManagement", "/user/users"),
        createBreadcrumb(
          "View Profile",
          "view-profile/:id",
          "viewProfile2",
          "",
          false
        ),
      ];
    }

    if (subSection === "users" && action === "edit") {
      return [
        createBreadcrumb("Users", "users", "userManagement", "/user/users"),
        createBreadcrumb("Edit", "edit/:id", "editIcon", "", false),
      ];
    }

    if (subSection === "requests" && action === "view-profile") {
      return [
        createBreadcrumb(
          "Requests",
          "requests",
          "viewProfile2",
          "/user/requests"
        ),
        createBreadcrumb(
          "View Profile",
          "view-profile/:id",
          "viewProfile2",
          "",
          false
        ),
      ];
    }

    if (subSection === "requests" && action === "edit") {
      return [
        createBreadcrumb(
          "Requests",
          "requests",
          "viewProfile2",
          "/user/requests"
        ),
        createBreadcrumb("Edit", "edit/:id", "editIcon", "", false),
      ];
    }
  }

  if (section === "organisations") {
    if (subSection === "add-organisation") {
      return [
        createBreadcrumb(
          "Organisations",
          "organisations",
          "companyIcon",
          "/organisations"
        ),
        createBreadcrumb(
          "Add New Organisation",
          "add-organisation",
          "companyIcon",
          "/organisations/add-organisation",
          false
        ),
      ];
    }

    if (subSection === "edit") {
      return [
        createBreadcrumb(
          "Organisations",
          "organisations",
          "companyIcon",
          "/organisations"
        ),
        createBreadcrumb("Edit", "edit/:id", "editIcon", "", false),
      ];
    }

    if (subSection) {
      return [
        createBreadcrumb(
          "Organisations",
          "organisations",
          "companyIcon",
          "/organisations"
        ),
        createBreadcrumb(
          "Organisation Details",
          ":id",
          "companyIcon",
          `/organisations/${subSection}`,
          false
        ),
      ];
    }

    return [
      createBreadcrumb(
        "Organisations",
        "organisations",
        "companyIcon",
        "/organisations"
      ),
    ];
  }

  if (section === "operators") {
    if (subSection) {
      return [
        createBreadcrumb("Operators", "operators", "operators", "/operators"),
        createBreadcrumb(
          "Operator Details",
          ":id",
          "operators",
          `/operators/${subSection}`,
          false
        ),
      ];
    }

    const operatorsBreadcrumb = findBreadcrumbByUrl("operators");
    return operatorsBreadcrumb ? [operatorsBreadcrumb] : [];
  }

  return [];
}

export function locationIdFinder(location: LocationLike) {
  return pathParts(location.pathname)[2];
}

export function locationFinderClient(location: LocationLike) {
  return pathParts(location.pathname)[1];
}

export function BreadCrumbNavigationGenerator(
  name: string,
  navigation: string,
  location: LocationLike
) {
  return navigation;
}

export function breadcrumbValuesHandle(
  name: string,
  navigation: string,
  location: LocationLike
) {
  const navigationMap: Record<string, string> = {
    "user/requests": "/user/requests",
    "leads/all-leads": "/leads/all-leads",
    "leads/hot-leads": "/leads/hot-leads",
    "leads/warm-leads": "/leads/warm-leads",
    "leads/interested-leads": "/leads/interested-leads",
    "leads/new-lead": "/leads/new-lead",
    "user-profile": "/user-profile",
  };

  if (
    navigation === "user/requests/view-profile/:id" ||
    navigation === "customers/:id" ||
    navigation === "all-leads/:id"
  ) {
    return location.pathname;
  }

  return navigationMap[navigation] || navigation;
}
